'use client';
import cn from 'classnames';
import { createContext, useState, ReactNode, useEffect } from 'react';
import Modal from 'react-modal';
import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock';

// Modal.setAppElement('#body');
export type ModalContextValue = {
  /** Shows whether a modal is currently open or not */
  isOpen: boolean;

  /** The current content of the modal */
  content: null | JSX.Element;

  /** Whether the root context provider is available or not */
  isAvailable: boolean;

  /** Show whether a modal is fullscreen */
  isFullScreen: boolean;

  /** anchor modal default to center of the screen. bottom is used on sign up mobile  */
  anchorPoint: 'center' | 'bottom';

  /** Sets the content of the modal and shows it to the user */
  showModal: (
    content: JSX.Element,
    options?: Partial<ModalContextValue>
  ) => void;

  /** Sets whether the modal is open or closed */
  setModalOpen: (isOpen: boolean) => void;

  /** Closes the modal */
  closeModal: () => void;

  /** Function to run when the modal is closed via button or overlay */
  onClose?: () => void;

  /** Adds a top level classname to the content so we can style the top level of the modal */
  topClassName?: string;

  /** Background Color Override */
  backgroundColor?: string;
};

const throwNotAvailable = () => {
  throw Error('<ModalProvider> not found. Please check the render tree');
};

export const ModalContext = createContext<ModalContextValue>({
  isOpen: false,
  content: null,
  isAvailable: false,
  isFullScreen: false,
  anchorPoint: 'center',
  showModal: throwNotAvailable,
  setModalOpen: throwNotAvailable,
  closeModal: throwNotAvailable,
  onClose: throwNotAvailable
});

const DEFAULT_MODAL_OPTIONS: Partial<ModalContextValue> = {
  anchorPoint: 'center',
  topClassName: ''
};

type ModalProviderProps = {
  /** Put the component tree in here. Server components are supported */
  children: ReactNode;
};

function ModalProvider(props: ModalProviderProps) {
  const { children } = props;

  const [state, setState] = useState<ModalContextValue>({
    isAvailable: true,
    content: null,
    isOpen: false,
    isFullScreen: false,
    anchorPoint:
      DEFAULT_MODAL_OPTIONS.anchorPoint as ModalContextValue['anchorPoint'],
    topClassName: DEFAULT_MODAL_OPTIONS.topClassName,
    showModal,
    setModalOpen,
    closeModal,
    onClose: () => {},
    backgroundColor: '#FBF9F3'
  });

  function showModal(
    content: JSX.Element,
    options: Partial<ModalContextValue> = DEFAULT_MODAL_OPTIONS
  ) {
    setState({
      ...state,
      content,
      isOpen: true,
      ...options
    });
  }

  function setModalOpen(isOpen: boolean) {
    setState({
      ...state,
      isOpen
    });
  }

  function closeModal() {
    state.onClose?.();
    setState({
      ...state,
      isOpen: false,
      content: null,
      isFullScreen: false,
      ...DEFAULT_MODAL_OPTIONS
    });
  }

  useEffect(() => {
    setTimeout(() => {
      const targetElement = document?.querySelector(
        '.ModalContext-content'
      ) as HTMLElement;
      // Create some effect here that stops the whole page from scrolling when
      // the modals are open
      if (state.isOpen) {
        disableBodyScroll(targetElement);
        return;
      }

      clearAllBodyScrollLocks();
    }, 0);

    if (state.isOpen) {
      // push to history when modal opens
      window.history.pushState(null, '', window.location.href);

      // close modal on browser 'back'
      window.onpopstate = () => {
        window.onpopstate = () => {};
        window.history.back();

        setState({
          ...state,
          isOpen: false,
          content: null,
          isFullScreen: false,
          ...DEFAULT_MODAL_OPTIONS
        });
      };
    }
  }, [state.isOpen]);

  return (
    <ModalContext.Provider value={state}>
      {children}
      <Modal
        isOpen={state.isOpen}
        className={cn(
          'ModalContext-content',
          {
            isFullScreen: state.isFullScreen
          },
          `anchor-${state.anchorPoint}`,
          state.topClassName
        )}
        overlayClassName='ModalContext-overlay'
        // TODO: Figure out how to setAppElement so
        // we can turn off these warnings/errors
        ariaHideApp={false}
        onRequestClose={closeModal}
        shouldCloseOnOverlayClick={true}
        style={{
          content: { backgroundColor: state.backgroundColor }
        }}
      >
        {state.content}
      </Modal>
    </ModalContext.Provider>
  );
}

export default ModalProvider;
